import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // How the customer chose to receive the order on a product page:
  // { method: 'shipping' | 'pickup', locationId }. The cart screen starts from it.
  const [fulfillmentPreference, setFulfillmentPreference] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('cartFulfillment')) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (fulfillmentPreference) localStorage.setItem('cartFulfillment', JSON.stringify(fulfillmentPreference));
      else localStorage.removeItem('cartFulfillment');
    } catch {
      // storage unavailable — the preference just won't persist
    }
  }, [fulfillmentPreference]);

  const addToCart = (product, preference = null) => {
    if (preference) setFulfillmentPreference(preference);
    // Any new item added invalidates a previous pending checkout
    sessionStorage.removeItem('pendingOrderId');
    sessionStorage.removeItem('pendingOrderType');
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevItems, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    setFulfillmentPreference(null);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalPrice,
    fulfillmentPreference,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
